<template>
  <div>
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="isOpen"
          class="side-sheet-overlay"
          @click.self="handleClose"
        />
      </Transition>

      <Transition
        name="slide"
        @after-leave="emit('closed')"
      >
        <div
          v-if="isOpen"
          class="side-sheet-panel"
          :class="{ 'side-sheet-panel--full-height': fullHeightMobile }"
          :style="{ '--side-sheet-desktop-width': width }"
        >
          <div class="side-sheet-header">
            <button
              v-if="showBack"
              class="side-sheet-back"
              aria-label="Back"
              data-cy="side-sheet-back"
              @click="emit('back')"
            >
              <ui-icon> arrow_back </ui-icon>
            </button>
            <h2
              class="side-sheet-title"
              :class="{ 'side-sheet-title--centered': showBack }"
            >
              {{ title }}
            </h2>
            <button
              class="side-sheet-close"
              aria-label="Close"
              @click="handleClose"
            >
              <ui-icon> close </ui-icon>
            </button>
          </div>

          <div class="side-sheet-content">
            <slot></slot>
          </div>

          <div
            v-if="actionText"
            class="side-sheet-actions"
          >
            <AcvButton
              ref="actionButtonTarget"
              data-testid="side-sheet__button"
              :disabled="isDisabled"
              :variant="actionVariant"
              @click="emit('onActionClick')"
            >
              {{ actionText }}
            </AcvButton>
            <AcvButton
              v-if="showCancelAction"
              data-testid="side-sheet__cancel-button"
              variant="tertiary"
              class="cancel-button"
              @click="handleClose"
            >
              {{ cancelText }}
            </AcvButton>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { watch } from 'vue';
import { AcvButton } from '@acv/shared-vue';
import { useOverlayScrollHelper } from '@/composables/useOverlayScrollHelper';
import { useWalkthroughTarget } from '@/composables/useWalkthroughTarget';

const emit = defineEmits(['onActionClick', 'toggleSideSheet', 'back', 'closed']);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: true,
  },
  // Optional: registers the action button as a walkthrough target under this stepId,
  // since it's rendered here rather than passed in by the caller.
  actionButtonStepId: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  showBack: {
    type: Boolean,
    default: false,
  },
  actionText: {
    type: String,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showCancelAction: {
    type: Boolean,
    default: false,
  },
  cancelText: {
    type: String,
    default: 'Cancel',
  },
  actionVariant: {
    type: String,
    default: 'primary',
  },
  // Desktop only (tw-md+) — mobile always renders full-width regardless of this.
  width: {
    type: String,
    default: '375px',
  },
  fullHeightMobile: {
    type: Boolean,
    default: false,
  },
});

const { preventBgFromScrolling, allowBgToScroll } = useOverlayScrollHelper();
const { registerModal, unregisterModal, isTopmostModal } = useModalStack();

const actionButtonTarget = props.actionButtonStepId
  ? useWalkthroughTarget(props.actionButtonStepId)
  : ref(null);

const handleClose = () => {
  emit('toggleSideSheet', false);
};

const handleEsc = (e) => {
  if (e.key === 'Escape' && props.isOpen && isTopmostModal()) {
    handleClose();
  }
};

watch(
  () => props.isOpen,
  (isSideSheetOpen) => {
    if (isSideSheetOpen) {
      preventBgFromScrolling();
      registerModal();
    } else {
      allowBgToScroll();
      unregisterModal();
    }
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener('keydown', handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEsc);
  if (props.isOpen) {
    allowBgToScroll();
    unregisterModal();
  }
});
</script>

<style lang="scss" scoped>
.side-sheet-overlay {
  @apply tw-fixed tw-inset-0 tw-z-50 tw-bg-black/50;
}

.side-sheet-panel {
  @apply tw-fixed tw-bottom-0 tw-right-0 tw-z-50 tw-bg-white tw-shadow-2xl;
  @apply tw-flex tw-flex-col;
  @apply tw-max-h-svh tw-w-full;

  // Opt-in via fullHeightMobile — see prop comment.
  &--full-height {
    @apply tw-top-0 tw-h-svh;
  }

  @screen tw-md {
    @apply tw-top-0 tw-h-svh;
    width: var(--side-sheet-desktop-width);
  }
}

.side-sheet-header {
  @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-8 tw-py-6;
  @apply tw-flex-shrink-0;
  border-bottom: 1px solid theme('colors.border.base');
}

.side-sheet-title {
  @apply tw-m-0 tw-text-xl tw-font-medium tw-leading-[30px] tw-tracking-[0.15px];

  &--centered {
    @apply tw-flex-1 tw-text-center;
  }
}

.side-sheet-close,
.side-sheet-back {
  @apply tw-flex tw-size-12 tw-items-center tw-justify-center;
  @apply tw-rounded-full;
  @apply tw-cursor-pointer tw-transition-colors;
  @apply tw-border-0 tw-bg-transparent;
}

.side-sheet-back {
  @apply tw-m-0 tw-justify-start tw-p-0;
}

.side-sheet-content {
  @apply tw-min-h-0 tw-px-4;
  @apply tw-overflow-auto;

  @screen tw-md {
    @apply tw-flex-1;
  }
}

.side-sheet-actions {
  @apply tw-flex tw-flex-shrink-0 tw-items-center tw-bg-white tw-p-4;
  button {
    min-width: 25%;
  }
}

.cancel-button {
  @apply tw-ml-4;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from {
  @apply tw-translate-y-full;

  @screen tw-md {
    @apply tw-translate-x-full tw-translate-y-0;
  }
}

.slide-leave-to {
  @apply tw-translate-y-full;

  @screen tw-md {
    @apply tw-translate-x-full tw-translate-y-0;
  }
}
</style>
